/**
 * @file     StepTransition.tsx
 * @purpose  Smooth enter/exit transition for swappable content: a fade and slide between steps (sideways in the direction of travel when an `order` is given), and the container height gliding to each step's size instead of jumping. CSS transitions, interruptible, reduced-motion safe.
 */
'use client';

import { useRef, useState, useEffect, useLayoutEffect } from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { motionTokens } from '@/design-system/tokens';

const { easing } = motionTokens;
const { heightDuration, overflowBuffer, enterDuration: defaultEnter, exitDuration: defaultExit } = motionTokens.stepTransition;

type Phase = 'idle' | 'exit' | 'enter';

// useLayoutEffect warns during SSR; the measurement only matters in the browser.
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

type StepTransitionProps = {
  /** Changing this key triggers the exit → enter cycle */
  activeKey: string;
  children: React.ReactNode;
  /** Direction of slide. 'up' = exit slides up, enter slides up from below. */
  direction?: 'up' | 'down';
  /** Step position. When set, steps slide sideways: forward enters from the end side, back from the start side. */
  order?: number;
  /** Custom enter duration in ms */
  enterDuration?: number;
  /** Custom exit duration in ms */
  exitDuration?: number;
};

export function StepTransition({
  activeKey,
  children,
  direction = 'up',
  order,
  enterDuration = defaultEnter,
  exitDuration = defaultExit,
}: StepTransitionProps) {
  const isRtl = useTheme().direction === 'rtl';
  const [displayedKey, setDisplayedKey] = useState(activeKey);
  const [displayedChildren, setDisplayedChildren] = useState(children);
  const [phase, setPhase] = useState<Phase>('idle');
  const [travel, setTravel] = useState<1 | -1>(direction === 'up' ? 1 : -1);
  const lastOrder = useRef(order);
  const transitioning = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // When children update for the current key, keep them in sync
  if (activeKey === displayedKey) {
    // Direct assignment during render is safe for derived state
    if (displayedChildren !== children) {
      setDisplayedChildren(children);
    }
  }

  useEffect(() => {
    if (activeKey === displayedKey) return;
    if (transitioning.current) {
      // Key changed mid-transition, fast forward
      if (timerRef.current) clearTimeout(timerRef.current);
    }

    transitioning.current = true;
    if (order !== undefined && lastOrder.current !== undefined) {
      setTravel(order < lastOrder.current ? -1 : 1);
    }
    lastOrder.current = order;

    // Phase 1: current content leaves toward the start side
    setPhase('exit');

    timerRef.current = setTimeout(() => {
      // Phase 2: swap content while hidden, parked on the far side
      setDisplayedKey(activeKey);
      setDisplayedChildren(children);
      setPhase('enter');

      // Two frames so the parked position paints before it glides in
      requestAnimationFrame(() => requestAnimationFrame(() => {
        setPhase('idle');
        transitioning.current = false;
      }));
    }, exitDuration);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [activeKey, displayedKey, children, exitDuration, order]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Height follows the content. It starts as `auto` (the server render), then
  // tracks the measured content height in px so CSS can interpolate between
  // steps. Overflow is clipped only while the height is moving, inside a
  // buffer so focus rings and shadows are not cut off.
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  const heightRef = useRef<number | null>(null);
  const [resizing, setResizing] = useState(false);
  const resizeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useIsomorphicLayoutEffect(() => {
    const node = contentRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const next = node.offsetHeight;
      const prev = heightRef.current;
      if (prev === next) return;
      heightRef.current = next;
      if (prev !== null) {
        setResizing(true);
        if (resizeTimer.current) clearTimeout(resizeTimer.current);
        resizeTimer.current = setTimeout(() => setResizing(false), heightDuration + 60);
      }
      setHeight(next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => {
      observer.disconnect();
      if (resizeTimer.current) clearTimeout(resizeTimer.current);
    };
  }, []);

  const offset = motionTokens.stepTransition.offset;
  const horizontal = order !== undefined;
  const sign = travel * (horizontal && isRtl ? -1 : 1);
  const shift = (px: number) => (horizontal ? `translate3d(${px}px, 0, 0)` : `translate3d(0, ${px}px, 0)`);
  const transform = phase === 'exit' ? shift(-sign * offset) : phase === 'enter' ? shift(sign * offset) : 'none';
  const transition = phase === 'enter'
    ? 'none'
    : phase === 'exit'
      ? `opacity ${exitDuration}ms ${easing.accelerate}, transform ${exitDuration}ms ${easing.accelerate}`
      : `opacity ${enterDuration}ms ${easing.decelerate}, transform ${enterDuration}ms ${easing.decelerate}`;

  return (
    <Box
      sx={{
        boxSizing: 'content-box',
        m: `-${overflowBuffer}px`,
        p: `${overflowBuffer}px`,
        height: height === null ? 'auto' : `${height}px`,
        overflow: resizing ? 'hidden' : 'visible',
        transition: `height ${heightDuration}ms ${easing.standard}`,
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      <Box
        ref={contentRef}
        sx={{
          opacity: phase === 'idle' ? 1 : 0,
          transform,
          transition,
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none !important',
            transform: 'none !important',
            opacity: '1 !important',
          },
        }}
      >
        {displayedChildren}
      </Box>
    </Box>
  );
}
